import { useAppSelector } from "@/store/store-hooks";
import {
  FaroIconButton,
  FaroIconButtonSizes,
  FaroText,
  OpenLinkIcon,
  grey,
} from "@faro-lotv/flat-ui";
import { isIElementImageCommand } from "@faro-lotv/ielement-types";
import {
  createSignedUrlForIElementUri,
  selectChildDepthFirst,
  useKeepSignedUrlsAlive,
  useValidSignedUrl,
} from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { useMemo, type JSX } from "react";
import { AnnotationWrapper } from "../annotation-wrapper";
import { AnnotationProps } from "../annotations-types";

/** @returns A renderer for an image annotation element */
export function ImageAnnotationRenderer({
  iElement,
  worldTransform,
}: AnnotationProps): JSX.Element | null {
  const linkToImage = useAppSelector(
    selectChildDepthFirst(iElement, isIElementImageCommand),
  );

  const { projectApiClient } = useApiClientContext();

  const signedUrl = useMemo(
    () =>
      linkToImage &&
      createSignedUrlForIElementUri(projectApiClient, linkToImage),
    [linkToImage, projectApiClient],
  );

  useKeepSignedUrlsAlive(signedUrl ? [signedUrl] : []);
  const validUrl = useValidSignedUrl(signedUrl);

  // If there is no image uri in the annotation element, do not render anything
  if (!linkToImage || !validUrl) return null;

  return (
    <AnnotationWrapper
      iElement={iElement}
      worldTransform={worldTransform}
      style={{ padding: 2 }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <FaroText variant="heading14" sx={{ color: grey[0] }} noWrap>
          {linkToImage.fileName}
        </FaroText>
        <FaroIconButton
          target="_blank"
          href={validUrl}
          color={grey[0]}
          size={FaroIconButtonSizes.s}
        >
          <OpenLinkIcon sx={{ width: 20, height: 20 }} />
        </FaroIconButton>
      </Stack>
      <img src={validUrl} alt={linkToImage.name} loading="lazy" width={400} />
    </AnnotationWrapper>
  );
}
